import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deleteCard, getStreamerCards, api } from '../api/apis';
import {
  Plus, Trash2, AlertTriangle, Tag, IndianRupee,
  Edit, Eye, Star, Users, Package, Gamepad, X, Upload, Save
} from 'lucide-react';
import { getFromLocalStorage, removeFromLocalStorage } from '../utils/localStorage';
import ProtectedRoute from '../Components/ProtectedRoute';

const CardInventory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [formData, setFormData] = useState({
    cardTitle: '',
    description: '',
    price: '',
    rarity: 'Common',
    category: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const fetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const user = getFromLocalStorage("user");
  console.log('Current User:', user);
  const token = getFromLocalStorage("token");
  const adminToken = getFromLocalStorage("adminToken");
  const isLoggedIn = useMemo(() =>
    (token && token.trim() !== "")
    , [token]);
  const isCreator = true; // Allow all authenticated users

  const handleLogout = useCallback(() => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("adminToken");
    removeFromLocalStorage("user");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const checkTimeout = useCallback(() => {
    const expirationTime = getFromLocalStorage("expirationTime");
    if (!expirationTime || Date.now() > parseInt(expirationTime, 10)) {
      removeFromLocalStorage("token");
      removeFromLocalStorage("adminToken");
      removeFromLocalStorage("user");
      removeFromLocalStorage("expirationTime");
      return false;
    }
    return true;
  }, []);
  const getRarityColor = useCallback((rarity) => {
    const colors = {
      Common: "text-gray-400 border-gray-600 bg-gray-900/20",
      Rare: "text-blue-400 border-blue-600 bg-blue-900/20",
      Epic: "text-purple-400 border-purple-600 bg-purple-900/20",
      Legendary: "text-yellow-400 border-yellow-600 bg-yellow-900/20",
      Mythic: "text-red-400 border-red-600 bg-red-900/20",
    };
    return colors[rarity] || "text-gray-400 border-gray-600 bg-gray-900/20";
  }, []);

  const getRarityGradient = useCallback((rarity) => {
    const gradients = {
      Common: "from-gray-500 to-gray-700",
      Rare: "from-blue-500 to-blue-700",
      Epic: "from-purple-500 to-purple-700",
      Legendary: "from-yellow-500 to-yellow-700",
      Mythic: "from-red-500 to-red-700",
    };
    return gradients[rarity] || "from-gray-500 to-gray-700";
  }, []);

  const rarityOptions = ['Common', 'Rare', 'Epic', 'Legendary', 'Mythic'];

  const handleEdit = (card) => {
    setEditingCard(card);
    setFormData({
      cardTitle: card.cardTitle || '',
      description: card.description || '',
      price: card.price || '',
      rarity: card.rarity || 'Common',
      category: Array.isArray(card.category) ? card.category.join(', ') : (card.category || '')
    });
    setImagePreview(card.imageUrl || '');
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingCard(null);
    setFormData({
      cardTitle: '',
      description: '',
      price: '',
      rarity: 'Common',
      category: ''
    });
    setImageFile(null);
    setImagePreview('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateCard = async (e) => {
    e.preventDefault();

    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      if (!token) {
        if (!window.__suppressAuthChecks) {
          toast.error("Please log in again");
          navigate("/login");
        }
        return;
      }

      const updateData = {
        cardTitle: formData.cardTitle,
        description: formData.description,
        price: parseFloat(formData.price),
        rarity: formData.rarity,
        category: formData.category.split(',').map(cat => cat.trim()).filter(cat => cat)
      };

      // If there's a new image, handle image upload first
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);

        const imageResponse = await api.post('/auth/admin/upload-image', imageFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
            "User-Info": user?.emailId || user?.username || user,
          },
        });

        if (imageResponse.data.imageUrl) {
          updateData.imageUrl = imageResponse.data.imageUrl;
        }
      }

      const response = await api.post(`/auth/user/editCard/${editingCard._id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user,
        },
      });

      if (response.data.status === "success") {
        toast.success("Card updated successfully!");
        // Update the card in the local state
        setCards(cards.map(card =>
          card._id === editingCard._id
            ? { ...card, ...updateData, imageUrl: updateData.imageUrl || card.imageUrl }
            : card
        ));
        handleCloseEditModal();
      } else {
        toast.error(response.data.message || "Failed to update card");
      }
    } catch (error) {
      console.error("Error updating card:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        toast.error("An error occurred while updating the card");
      }
    }
  };

  const confirmDeleteCard = (cardId) => {
    setCardToDelete(cardId);
    setShowConfirmModal(true);
  };

  const handleDeleteCard = useCallback(async () => {
    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      if (!token) {
        if (!window.__suppressAuthChecks) {
          toast.error("Please log in again");
          navigate("/login");
        }
        return;
      }

      const headers = {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user,
        },
      };

      const response = await deleteCard(cardToDelete, headers);

      if (response.data.status === "success") {
        toast.success("Card deleted successfully!");
        setCards(cards.filter((card) => card._id !== cardToDelete));
      } else {
        toast.error(response.data.message || "Failed to delete card");
      }
    } catch (error) {
      console.error("Error deleting card:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        toast.error("An error occurred while deleting the card");
      }
    } finally {
      setCardToDelete(null);
      setShowConfirmModal(false);
    }
  }, [checkTimeout, handleLogout, cardToDelete]);

  const fetchCards = useCallback(async () => {
    if (fetchedRef.current) return;

    if (!checkTimeout()) {
      handleLogout();
      toast.error("Session expired. Please log in again.");
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    const header = {
      headers: {
        Authorization: `Bearer ${token}`,
        "User-Info": user?.emailId || user?.username || user,
      },
      signal: controller.signal
    };

    try {
      const identifier = user?.emailId || user?.username || user;
      const cleanIdentifier = typeof identifier === 'string' ? identifier.trim() : identifier;
      const response = await getStreamerCards(cleanIdentifier, header);
      if (!controller.signal.aborted) {
        if (response.data.status === "success") {
          setCards(response.data.cards || []);
        } else {
          toast.error(response.data.error || "Failed to fetch cards.");
        }
        fetchedRef.current = true;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error("Error fetching cards:", error);
        if (error.response?.status === 401) {
          handleLogout();
        } else {
          toast.error("An unexpected error occurred while fetching cards.");
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [navigate, token, user, checkTimeout, handleLogout]);

  useEffect(() => {
    // Simple effect to fetch cards when user is available
    // We use a local flag to prevent race conditions if needed, but fetchCards handles abort controller
    if (user) {
      fetchCards();
    }
    // No cleanup needed for the call itself as fetchCards handles it via AbortController
  }, [fetchCards, user]);

  // FontAwesome CDN for icons
  useEffect(() => {
    if (!document.getElementById("fa-cdn")) {
      const link = document.createElement("link");
      link.id = "fa-cdn";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css";
      link.crossOrigin = "anonymous";
      link.referrerPolicy = "no-referrer";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    Card Inventory
                  </h1>
                  <p className="text-gray-400">Manage your created cards and create new ones (Creator Only)</p>
                </div>
              </div>
            </div>

            {/* Cards Section */}
            <div className="mb-8 bg-black/30 rounded-xl p-6 border border-gray-800">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">Your Cards</h2>
                  <p className="text-gray-400">Cards you've created for your audience</p>
                </div>
                <button
                  onClick={() => navigate("/create-card")}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-3 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-colors flex items-center space-x-2 shadow-lg shadow-yellow-500/25"
                >
                  <Plus className="w-5 h-5" />
                  <span>Create New Card</span>
                </button>
              </div>

              {cards.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">No cards created yet</h3>
                  <p className="text-gray-500 mb-2">Start creating your first challenge card!</p>
                  <button
                    onClick={() => navigate("/create-card")}
                    className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-3 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-colors flex items-center space-x-2 shadow-lg shadow-yellow-500/25 mx-auto"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Create Your First Card</span>
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {cards.map((card, index) => (
                    <div
                      key={card._id || index}
                      className="bg-black/30 rounded-xl p-6 border border-gray-800 hover:border-yellow-500/50 transition-colors group backdrop-blur-sm"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex flex-col h-full">
                        {/* Card Header */}
                        <div className="flex justify-between items-start mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getRarityColor(card.rarity)}`}>
                            {card.rarity}
                          </span>
                        </div>

                        {/* Card Image */}
                        <div className="relative mb-4 overflow-hidden rounded-lg">
                          <img
                            src={card.imageUrl || "https://picsum.photos/300/200"}
                            alt={card.cardTitle}
                            className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                            style={{ maxWidth: '100%', height: '192px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.src = "https://picsum.photos/300/200";
                            }}
                          />
                        </div>

                        {/* Card Info */}
                        <div className="flex-grow">
                          <h3 className="text-lg font-bold mb-2 text-white group-hover:text-yellow-400 transition-colors">
                            {card.cardTitle}
                          </h3>
                          <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                            {card.description}
                          </p>
                        </div>

                        {/* Card Footer */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Tag className="w-4 h-4" />
                            <span>{Array.isArray(card.category) ? card.category.join(", ") : card.category}</span>
                          </div>
                          <div className="flex items-center gap-2 text-lg font-bold text-yellow-400">
                            <IndianRupee className="w-5 h-5" />
                            <span>{card.price}</span>
                          </div>
                          <div className="flex space-x-2 mt-4">
                            <button
                              onClick={() => handleEdit(card)}
                              className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1"
                            >
                              <Edit className="w-4 h-4" />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => confirmDeleteCard(card._id)}
                              className="flex-1 bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 text-white font-semibold py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center space-x-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Edit Modal */}
            {showEditModal && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-black/90 backdrop-blur-xl border border-gray-700 rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">
                      Edit Card
                    </h2>
                    <button
                      onClick={handleCloseEditModal}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleUpdateCard} className="space-y-6">
                    {/* Image Upload */}
                    <div>
                      <label className="block text-sm font-semibold text-yellow-400 mb-2">
                        Card Image
                      </label>
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="edit-image-upload"
                        />
                        <label
                          htmlFor="edit-image-upload"
                          className="w-[150px] h-[100px] bg-gray-800/50 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-yellow-500/50 transition-all flex items-center justify-center overflow-hidden"
                        >
                          {imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="object-cover"
                              style={{ width: '150px', height: '100px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="text-center">
                              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                              <p className="text-gray-400">Click to upload image</p>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-semibold text-yellow-400 mb-2">
                        Card Title
                      </label>
                      <input
                        type="text"
                        value={formData.cardTitle}
                        onChange={(e) => setFormData(prev => ({ ...prev, cardTitle: e.target.value }))}
                        className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                        placeholder="Enter card title..."
                        required
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-semibold text-yellow-400 mb-2">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={4}
                        className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
                        placeholder="Enter card description..."
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Price */}
                      <div>
                        <label className="block text-sm font-semibold text-yellow-400 mb-2">
                          Price (₹)
                        </label>
                        <input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                          className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                          placeholder="Enter price..."
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>

                      {/* Rarity */}
                      <div>
                        <label className="block text-sm font-semibold text-yellow-400 mb-2">
                          Rarity
                        </label>
                        <select
                          value={formData.rarity}
                          onChange={(e) => setFormData(prev => ({ ...prev, rarity: e.target.value }))}
                          className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition-colors"
                        >
                          {rarityOptions.map(rarity => (
                            <option key={rarity} value={rarity}>{rarity}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-semibold text-yellow-400 mb-2">
                        Category
                      </label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 bg-black/30 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                        placeholder="Enter categories (comma separated)..."
                      />
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center space-x-2"
                      >
                        <Save className="w-4 h-4" />
                        <span>Update Card</span>
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseEditModal}
                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && (
              <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                <div className="bg-black/90 rounded-xl p-8 border border-gray-700 w-full max-w-md mx-auto shadow-2xl">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <AlertTriangle className="w-10 h-10 text-red-400" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4 text-white">Confirm Deletion</h2>
                    <p className="text-gray-300 mb-8 text-lg">Are you sure you want to delete this card? This action cannot be undone.</p>
                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={handleDeleteCard}
                        className="bg-red-600 hover:bg-red-700 px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center space-x-2 text-white shadow-lg hover:shadow-red-500/25"
                      >
                        <Trash2 className="w-5 h-5" />
                        <span>Yes, Delete</span>
                      </button>
                      <button
                        onClick={() => setShowConfirmModal(false)}
                        className="bg-gray-600 hover:bg-gray-700 px-8 py-3 rounded-lg font-semibold transition-all duration-200 text-white shadow-lg"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default CardInventory;
